// @ts-strict-ignore
import {
  FullPropertyType,
  PropertyType,
  ResourceType,
  TYPECAST_LIST_PROPERTY_SUBTYPES,
  TYPECAST_PROPERTY_TYPES,
} from 'common/report/constants';
import {
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {SectionItem} from 'common/components/items-menu/types';
import type {TooltipAttrs} from 'common/components/tooltip/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import segmentationSvg from 'common/assets/images/query/segmentation.svg';
import filterSvg from 'common/assets/images/query/filter.svg';
import {getIconForPropertyType, getLabelForPropertyType} from 'common/report/util/properties';
import {ProfileHistoryType, ProfileHistoryTypeToDisplayName} from 'common/types/reports/query';
import {ROLLING_WINDOW_SIZE_OPTIONS} from 'common/report/insights/models/constants';
import {RollingWindowSize} from 'common/util';
import customNumberImg from 'common/assets/images/custom-groups/custom-number.svg';
import customDateImg from 'common/assets/images/custom-groups/custom-date.svg';
import customStringImg from 'common/assets/images/custom-groups/custom-string.svg';
import {Translatable} from 'common/i18n';
import {translatable} from 'common/i18n/scope';

import {AggregationLabelParams} from '../types/shared-types';
import {Calculation, CalculationTypes, SelectorType, SelectorTypeAggregation, SelectorTypeDefinitions} from './types';
import translationKeys from './index.i18n';

export enum ControlBarAction {
  Filter = `filter`,
  FirstTimeFilter = `firstTimeFilter`,
  Rename = `rename`,
  Duplicate = `duplicate`,
  Delete = `delete`,
  CustomizeBuckets = `customizeBuckets`,
  GroupData = `groupData`,
  AddMultiAttribution = `addMultiAttribution`,
  AddMultiAttributionBehaviorFilter = `addMultiAttributionBehaviorFilter`,
  AddGroupedEvent = `addGroupedEvent`,
  ToggleAnyOrder = `toggleAnyOrder`,
  MapLookupTable = `mapLookupTable`,
  UnmapLookupTable = `unmapLookupTable`,
  Typecast = `typecast`,
  ProfileHistory = `profileHistory`,
  AddScdMeasurements = `addScdMeasurements`,
  SetLookbackPeriod = `setLookbackPeriod`,
  UnsetLookbackPeriod = `unsetLookbackPeriod`,
  RemoveScdMeasurements = `removeScdMeasurements`,
  RollingAverage = `rollingAverage`,
  Cumulative = `cumulative`,
  Show = `show`,
  Hide = `hide`,
  Save = `save`,
  Reset = `reset`,
  ShowTrendline = `showTrendline`,
  HideTrendline = `hideTrendline`,
}

type TypeBasedAction = ControlBarAction.Typecast | ControlBarAction.GroupData | ControlBarAction.ProfileHistory;
export enum ControlSelectEntryPoint {
  CustomBucketSubItem = `customBucketSubItem`,
  ControlBar = `controlBar`,
}

type ProfileHistoryControlBarActionValue = {action: ControlBarAction.ProfileHistory; toType: ProfileHistoryType};

export type ControlBarActionValue =
  | {action: ControlBarAction.Typecast; toType: FullPropertyType}
  | {action: ControlBarAction.ProfileHistory; toType: ProfileHistoryType}
  | {action: ControlBarAction.RollingAverage | ControlBarAction.Cumulative; calculation: Calculation}
  | {
      action: Exclude<
        ControlBarAction,
        | ControlBarAction.Typecast
        | ControlBarAction.ProfileHistory
        | ControlBarAction.RollingAverage
        | ControlBarAction.Cumulative
      >;
    };

const COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS = {size: DisplaySize.Small, 'delay-out': 500};

export type ControlBarActionItem = SectionItem<
  ControlBarActionValue,
  {order?: number | null; sectionLabel?: string | null; section?: number | null}
>;

// @todo(stefan) Fix this typing. It's super messed up and doesn't work at all for strict typing.
type ControlBarActionItemsType =
  | Record<Exclude<ControlBarAction, TypeBasedAction>, ControlBarActionItem>
  | Record<TypeBasedAction, (currentType: FullPropertyType, props?: Obj) => ControlBarActionItem>
  | Record<
      ControlBarAction.ProfileHistory,
      ({
        currentProfileHistoryType,
        hideEvents,
        selectorType,
      }: {
        currentProfileHistoryType: ProfileHistoryType;
        hideEvents: boolean;
        selectorType: SelectorTypeDefinitions;
      }) => SectionItem<ProfileHistoryControlBarActionValue>
    >
  | Record<
      ControlBarAction.RollingAverage,
      ({
        dateUnit,
        currentRollingWindowSize,
      }: {
        dateUnit: RollingWindowSize;
        currentRollingWindowSize: number;
      }) => SectionItem<ControlBarActionValue>
    >;

export const ControlBarActionItems: ControlBarActionItemsType = {
  // BEGIN first section
  [ControlBarAction.Reset]: {
    value: {action: ControlBarAction.Reset},
    label: translatable(translationKeys, ({t}) => t(`common.reset`)),
    icon: `undo`,
  },
  [ControlBarAction.Save]: {
    value: {action: ControlBarAction.Save},
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.save`)),
    icon: `save`,
  },
  [ControlBarAction.SetLookbackPeriod]: {
    section: 0,
    order: -1,
    value: {action: ControlBarAction.SetLookbackPeriod},
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.set-lookback-period`)),
    icon: `aggregate`,
  },
  [ControlBarAction.UnsetLookbackPeriod]: {
    section: 0,
    order: -1,
    value: {action: ControlBarAction.UnsetLookbackPeriod},
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.unset-lookback-period`)),
    icon: `trashcan`,
  },
  [ControlBarAction.Filter]: {
    section: 0,
    order: 0,
    value: {action: ControlBarAction.Filter},
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.filter`)),
    icon: `filter-xs`,
  },
  [ControlBarAction.FirstTimeFilter]: {
    section: 0,
    order: 1,
    value: {action: ControlBarAction.FirstTimeFilter},
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.first-time-filter`)),
    icon: `time`,
    tooltip: {
      text: translatable(translationKeys, ({t}) => t(`query-builder.actions.first-time-filter-tooltip`)),
      size: DisplaySize.Small,
    },
  },
  // END first section
  [ControlBarAction.Rename]: {
    value: {action: ControlBarAction.Rename},
    icon: `edit`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.rename`)),
  },
  [ControlBarAction.CustomizeBuckets]: {
    value: {action: ControlBarAction.CustomizeBuckets},
    icon: `bucketing`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.customize-range`)),
  },
  [ControlBarAction.GroupData]: (
    currentType: FullPropertyType,
    {hasTextAnalysis}: {hasTextAnalysis?: boolean} = {},
  ) => {
    const simpleType = currentType.isList ? PropertyType.List : currentType.type;

    return {
      value: {action: ControlBarAction.GroupData},
      icon: `custom-segments`,
      label: translatable(translationKeys, ({t}) =>
        hasTextAnalysis ? t(`query-builder.actions.group-data`) : t(`query-builder.actions.customize-buckets`),
      ),
      tooltip: {
        [PropertyType.Number]: {
          ...COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS,
          text: translatable(
            translationKeys,
            ({t}) =>
              `${t(`query-builder.actions.customize-buckets-tooltip-numeric`)} [${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#numeric-property-use-cases)`,
          ),
          'image-src': customNumberImg,
        },
        [PropertyType.Datetime]: {
          ...COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS,
          text: translatable(translationKeys, ({t}) => t(`query-builder.actions.customize-buckets-tooltip-datetime`)),
          'image-src': customDateImg,
        },
        [PropertyType.String]: {
          ...COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS,
          text: translatable(translationKeys, ({t}) => t(`query-builder.actions.customize-buckets-tooltip-string`)),
          'image-src': customStringImg,
        },
        [PropertyType.List]: {
          ...COMMON_CUSTOMIZE_GROUPS_TOOLTIP_PROPS,
          text: translatable(translationKeys, ({t}) => t(`query-builder.actions.customize-buckets-tooltip-string`)),
          'image-src': customStringImg,
        },
      }[simpleType],
    };
  },
  [ControlBarAction.AddMultiAttribution]: {
    section: 1,
    value: {action: ControlBarAction.AddMultiAttribution},
    icon: `attribution-string`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.attribution`)),
    tooltip: {
      text: translatable(translationKeys, ({t}) => t(`query-builder.actions.attribution-tooltip`)),
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.AddMultiAttributionBehaviorFilter]: {
    value: {action: ControlBarAction.AddMultiAttributionBehaviorFilter},
    icon: `filter-xs`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.filter-touchpoints`)),
    tooltip: {
      text: translatable(translationKeys, ({t}) => t(`query-builder.actions.filter-touchpoints-tooltip`)),
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.AddGroupedEvent]: {
    section: 2,
    value: {action: ControlBarAction.AddGroupedEvent},
    icon: `event`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.event-comparison`)),
    tooltip: {
      text: translatable(translationKeys, ({t}) => t(`query-builder.actions.event-comparison-tooltip`)),
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.ToggleAnyOrder]: {
    // section: 2,
    value: {action: ControlBarAction.ToggleAnyOrder},
    icon: `behavior-event-any-order`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.toggle-any-order`)),
    tooltip: {
      text: translatable(translationKeys, ({t}) => t(`query-builder.actions.toggle-any-order-tooltip`)),
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.MapLookupTable]: {
    value: {action: ControlBarAction.MapLookupTable},
    icon: `lookup`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.map-lookup-table`)),
    tooltip: {
      text: translatable(translationKeys, ({t}) => t(`query-builder.actions.map-lookup-table-tooltip`)),
      size: DisplaySize.Small,
    },
  },
  [ControlBarAction.UnmapLookupTable]: {
    value: {action: ControlBarAction.UnmapLookupTable},
    icon: `lookup`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.unmap-lookup-table`)),
  },
  [ControlBarAction.AddScdMeasurements]: {
    value: {action: ControlBarAction.AddScdMeasurements},
    icon: `calendar`,
    label: ({print}) => print(`Add Time and Measurement`),
  },
  [ControlBarAction.RemoveScdMeasurements]: {
    value: {action: ControlBarAction.RemoveScdMeasurements},
    icon: `trashcan`,
    label: ({print}) => print(`Remove Time and Measurement`),
  },
  [ControlBarAction.Typecast]: (
    currentType: FullPropertyType,
    {disabledTypes = []}: {disabledTypes?: PropertyType[]} = {},
  ) => ({
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.typecast`)),
    icon: getIconForPropertyType(currentType),
    subSections: (() => {
      const getTypecastItems = (types: PropertyType[], isList: boolean) =>
        types.map((type) => {
          if (disabledTypes.includes(type)) {
            return null;
          }

          return {
            icon: !isList && getIconForPropertyType(type),
            isSelected: type === currentType.type && isList === currentType.isList,
            label: getLabelForPropertyType(type),
            value: {
              action: ControlBarAction.Typecast as ControlBarAction.Typecast,
              toType: {
                type,
                isList,
              },
            },
          };
        });

      const listItem = {
        icon: getIconForPropertyType(PropertyType.List),
        label: getLabelForPropertyType(PropertyType.List),
        subSections: [
          {
            items: getTypecastItems(TYPECAST_LIST_PROPERTY_SUBTYPES, true),
          },
        ],
      };
      const supportsList = !disabledTypes.includes(PropertyType.List);

      return [
        {
          items: [...getTypecastItems(TYPECAST_PROPERTY_TYPES, false), supportsList && listItem].filter(Boolean),
        },
      ];
    })(),
  }),
  [ControlBarAction.ProfileHistory]: ({
    currentProfileHistoryType,
    hideEvents,
    selectorType,
  }: {
    currentProfileHistoryType: ProfileHistoryType;
    hideEvents: boolean;
    selectorType: SelectorTypeDefinitions;
  }) => {
    const action = ControlBarAction.ProfileHistory as const;
    const subSection = {
      items: [
        {
          label: ProfileHistoryTypeToDisplayName[ProfileHistoryType.Latest],
          value: {action, toType: ProfileHistoryType.Latest},
          // falsy profileHistoryType uses default behavior which is "Latest"
          isSelected: currentProfileHistoryType === ProfileHistoryType.Latest || !currentProfileHistoryType,
        },
      ],
    };

    if (!hideEvents) {
      subSection.items.unshift({
        label: ProfileHistoryTypeToDisplayName[ProfileHistoryType.EventTime],
        value: {action, toType: ProfileHistoryType.EventTime},
        isSelected: currentProfileHistoryType === ProfileHistoryType.EventTime,
      });
    } else if (selectorType !== SelectorTypeDefinitions.Segment) {
      subSection.items.push({
        label: ProfileHistoryTypeToDisplayName[ProfileHistoryType.DateRange],
        value: {action, toType: ProfileHistoryType.DateRange},
        isSelected: currentProfileHistoryType === ProfileHistoryType.DateRange,
      });
    }

    return {
      label: translatable(translationKeys, ({t}) => t(`query-builder.actions.profile-history-type`)),
      icon: `brand-cohorts`,
      subSections: [subSection],
    };
  },
  [ControlBarAction.RollingAverage]: ({
    dateUnit,
    currentRollingWindowSize,
  }: {
    dateUnit: RollingWindowSize;
    currentRollingWindowSize: number;
  }) => {
    return {
      sectionLabel: `Calculation Options`,
      section: 0,
      label: `Rolling Average`,
      icon: `formula`,
      subSections: [
        {
          items: ROLLING_WINDOW_SIZE_OPTIONS[dateUnit].map((size) => ({
            label: `${size} ${dateUnit}s`,
            value: {
              action: ControlBarAction.RollingAverage,
              calculation: {
                calculationType: CalculationTypes.Rolling,
                rollingWindowSize: size,
              },
            },
            tooltip: {
              size: DisplaySize.Small,
              placement: Placement.Right,
              text: `Rolling smooths trends by calculating the average value over previous ${size} ${dateUnit}s period`,
            },
            isSelected: size === currentRollingWindowSize,
          })),
        },
      ],
    } satisfies SectionItem<ControlBarActionValue>;
  },
  [ControlBarAction.Cumulative]: {
    sectionLabel: `Calculation Options`,
    section: 0,
    value: {
      action: ControlBarAction.Cumulative,
      calculation: {
        calculationType: CalculationTypes.Cumulative,
      },
    },
    icon: `formula`,
    label: `Cumulative`,
    tooltip: {
      text: `Cumulative adds each new period’s value to the sum of previous periods.`,
      size: DisplaySize.Small,
    },
  },

  // BEGIN last section
  [ControlBarAction.Delete]: {
    section: 3,
    value: {action: ControlBarAction.Delete},
    order: 1,
    icon: `x`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.remove`)),
  },
  [ControlBarAction.Duplicate]: {
    section: 3,
    value: {action: ControlBarAction.Duplicate},
    order: 2,
    icon: `duplicate`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.duplicate`)),
  },
  [ControlBarAction.Show]: {
    section: 3,
    order: 3, // shows last in section
    value: {action: ControlBarAction.Show},
    icon: `eye-hide`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.show-metric`)),
  },
  [ControlBarAction.Hide]: {
    section: 3,
    order: 3, // shows last in section
    value: {action: ControlBarAction.Hide},
    icon: `eye-hide`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.hide-metric`)),
  },
  [ControlBarAction.ShowTrendline]: {
    value: {action: ControlBarAction.ShowTrendline},
    icon: `line`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.show-trendline`)),
  },
  [ControlBarAction.HideTrendline]: {
    value: {action: ControlBarAction.HideTrendline},
    icon: `line`,
    label: translatable(translationKeys, ({t}) => t(`query-builder.actions.hide-trendline`)),
  },
  // END last section
};

export const DefaultCustomBucket: Bucket = {
  bucketSize: 10,
  disabled: false,
  groups: null,
  min: 0,
  max: 100,
  offset: null,
  unit: null,
};

export const DefaultCustomTCCBucket: Bucket = {
  bucketSize: 10,
  disabled: false,
  groups: null,
  min: 0,
  max: 100,
  offset: null,
  unit: `day`,
};

export const commonAddEntryTooltipAttrs = {
  size: DisplaySize.Small,
  placement: Placement.Right,
};

export const TutorialTooltips: {[key in SelectorTypeDefinitions]?: TooltipAttrs} = {
  [SelectorTypeDefinitions.Segment]: {
    'image-src': segmentationSvg,
    text: translatable(translationKeys, ({t}) => t(`query-builder.breakdown-tooltip`)),
    ...commonAddEntryTooltipAttrs,
  },
  [SelectorTypeDefinitions.Filter]: {
    'image-src': filterSvg,
    text: translatable(translationKeys, ({t}) => t(`query-builder.filter-tooltip`)),
    ...commonAddEntryTooltipAttrs,
  },
};

const HELP_DOCS_LINK: Translatable = translatable(
  translationKeys,
  ({t}) => `\n[${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/analysis/reports/insights#measurements)`,
);

const EventAggregations: SelectorTypeAggregation[] = [
  {
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.count-menu.total-events`)),
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: () =>
      translatable(
        translationKeys,
        ({t, print}) => t(`query-builder.count-menu.total-events-tooltip`) + print(HELP_DOCS_LINK),
      ),
  },
  {
    label: ({pluralGroupTitle}) =>
      translatable(translationKeys, ({t, print}) =>
        t(`query-builder.count-menu.unique-dg`, {dataGroupName: print(pluralGroupTitle)}),
      ),
    operator: PRIMITIVE_MATH_TYPES.UNIQUE,
    description: ({pluralGroup}: AggregationLabelParams) =>
      translatable(
        translationKeys,
        ({t, print}) =>
          t(`query-builder.count-menu.unique-dg-tooltip`, {dataGroupName: print(pluralGroup)}) + print(HELP_DOCS_LINK),
      ),
  },
  {
    label: ({group}: AggregationLabelParams) => `Total per ${group}`,
    subLabel: () => `Distribution, Average, Percentiles`,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}: AggregationLabelParams) => `The number of events performed per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        label: () => `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Average across all users of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: () => `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `A distribution of the number of times an event is performed per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: () => `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) => `Median of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: () => `25th Percentile`,
        operator: NUMERIC_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: () => `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: () => `75th Percentile`,
        operator: NUMERIC_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: () => `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: () => `90th Percentile`,
        operator: NUMERIC_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: () => `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the event count per ${group}${HELP_DOCS_LINK}`,
      },
      {
        label: () => `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
      },
      {
        label: () => `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the number of time a ${group} performed an event${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `Daily Active ${pluralGroupTitle} (DAU)`,
    operator: XAU_MATH_TYPES.DAU,
    shortLabel: () => `DAU`,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event within the last 24 hours${HELP_DOCS_LINK}`,
  },
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `Weekly Active ${pluralGroupTitle} (WAU)`,
    operator: XAU_MATH_TYPES.WAU,
    shortLabel: () => `WAU`,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event within the last 7 days${HELP_DOCS_LINK}`,
  },
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `Monthly Active ${pluralGroupTitle} (MAU)`,
    operator: XAU_MATH_TYPES.MAU,
    shortLabel: () => `MAU`,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event within the last 30 days${HELP_DOCS_LINK}`,
  },
  {
    label: () => `Count Sessions`,
    operator: PRIMITIVE_MATH_TYPES.SESSIONS,
    description: () => `The number of sessions that contain the selected event${HELP_DOCS_LINK}`,
    shortLabel: () => `Sessions`,
  },
];

const UserAggregations: SelectorTypeAggregation[] = [
  {
    label: () => `Total`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: () => `The number of profiles`,
  },
];

/** Behavior specific property aggregations */
const BehaviorPropertyAggregations: SelectorTypeAggregation[] = [
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.behavior-agg.most-frequent`)),
    operator: PRIMITIVE_MATH_TYPES.MOST_FREQUENT,
    shortLabel: () => `Most frequent`,
    description: () =>
      translatable(
        translationKeys,
        ({t, print}) => `${t(`query-builder.behavior-agg.most-frequent-tooltip`)}${print(HELP_DOCS_LINK)}`,
      ),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.behavior-agg.first-value`)),
    operator: PRIMITIVE_MATH_TYPES.FIRST_VALUE,
    shortLabel: () => `First value`,
    description: () =>
      translatable(
        translationKeys,
        ({t, print}) => `${t(`query-builder.behavior-agg.first-value-tooltip`)}${print(HELP_DOCS_LINK)}`,
      ),
  },
];

export const EventPropertyAggregations: SelectorTypeAggregation[] = [
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.sum`)),
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.sum-tooltip`)),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.average`)),
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    shortLabel: () => `Average`,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.average-tooltip`)),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.median`)),
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.median-tooltip`)),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.distinct-count`)),
    operator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    shortLabel: () => `Distinct Count`,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.distinct-count-tooltip`)),
  },
  /** @todo [pablo] have a dedicated list specific for behavioral aggregations */
  ...BehaviorPropertyAggregations,
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.p25`)),
    operator: PRIMITIVE_MATH_TYPES.P25,
    shortLabel: () => `P25`,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.p25-tooltip`)),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.p75`)),
    operator: PRIMITIVE_MATH_TYPES.P75,
    shortLabel: () => `P75`,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.p75-tooltip`)),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.p90`)),
    operator: PRIMITIVE_MATH_TYPES.P90,
    shortLabel: () => `P90`,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.p90-tooltip`)),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.p99`)),
    operator: PRIMITIVE_MATH_TYPES.P99,
    shortLabel: () => `P99`,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.p99-tooltip`)),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.minimum`)),
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.minimum-tooltip`)),
  },
  {
    forProperty: true,
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.maximum`)),
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description: () => translatable(translationKeys, ({t}) => t(`query-builder.property-agg.maximum-tooltip`)),
  },
];

const PerUserEventPropertyAggregations: SelectorTypeAggregation[] = [
  {
    forProperty: true,
    label: () => `Sum`,
    shortLabel: ({group}: AggregationLabelParams) => `Sum per ${group}`,
    perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
    description: ({group}: AggregationLabelParams) =>
      `The total of numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: () => `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Average of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the sum of property values${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Median of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: () => `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: () => `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        shortLabel: () => `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PER_USER_AGGREGATIONS.TOTAL,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the sum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    forProperty: true,
    label: () => `Average`,
    shortLabel: ({group}: AggregationLabelParams) => `Average per ${group}`,
    perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
    description: ({group}: AggregationLabelParams) =>
      `Average of numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: () => `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Sum of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Average of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the average of property values${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Median of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        shortLabel: () => `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        shortLabel: () => `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        shortLabel: () => `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PER_USER_AGGREGATIONS.AVERAGE,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the average of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    forProperty: true,
    label: () => `Distinct Count`,
    shortLabel: ({group}: AggregationLabelParams) => `Distinct Count per ${group}`,
    perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
    description: ({group}: AggregationLabelParams) =>
      `The number of distinct property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: () => `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `Average of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `Sum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the distinct property value counts${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `The total of the distinct property values counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        shortLabel: () => `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        shortLabel: () => `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        shortLabel: () => `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PER_USER_AGGREGATIONS.UNIQUE_VALUES,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the distinct property value counts per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    forProperty: true,
    label: () => `Minimum`,
    shortLabel: ({group}: AggregationLabelParams) => `Minimum per ${group}`,
    perUserOperator: PER_USER_AGGREGATIONS.MIN,
    description: ({group}: AggregationLabelParams) =>
      `Minimum of all the numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: () => `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PER_USER_AGGREGATIONS.MIN,
        description: ({group}: AggregationLabelParams) =>
          `Sum of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PER_USER_AGGREGATIONS.MIN,
        description: ({group}: AggregationLabelParams) =>
          `Average of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PER_USER_AGGREGATIONS.MIN,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the minimum of property values${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PER_USER_AGGREGATIONS.MIN,
        description: ({group}: AggregationLabelParams) =>
          `Median of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.MIN,
        shortLabel: () => `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.MIN,
        shortLabel: () => `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.MIN,
        shortLabel: () => `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Maximum`,
        operator: PRIMITIVE_MATH_TYPES.MAX,
        perUserOperator: PER_USER_AGGREGATIONS.MIN,
        description: ({group}: AggregationLabelParams) =>
          `Maximum of the minimum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
  {
    forProperty: true,
    label: () => `Maximum`,
    shortLabel: ({group}: AggregationLabelParams) => `Maximum per ${group}`,
    perUserOperator: PER_USER_AGGREGATIONS.MAX,
    description: ({group}: AggregationLabelParams) =>
      `Maximum of all the numeric property values per ${group}${HELP_DOCS_LINK}`,
    subAggregations: [
      {
        forProperty: true,
        label: () => `Sum`,
        operator: PRIMITIVE_MATH_TYPES.TOTAL,
        perUserOperator: PER_USER_AGGREGATIONS.MAX,
        description: ({group}: AggregationLabelParams) =>
          `Sum of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Average`,
        operator: PRIMITIVE_MATH_TYPES.AVERAGE,
        perUserOperator: PER_USER_AGGREGATIONS.MAX,
        description: ({group}: AggregationLabelParams) => `Average of the maximum of property values per ${group}`,
      },
      {
        forProperty: true,
        label: () => `Distribution`,
        operator: PRIMITIVE_MATH_TYPES.HISTOGRAM,
        perUserOperator: PER_USER_AGGREGATIONS.MAX,
        description: ({pluralGroup}: AggregationLabelParams) =>
          `A distribution of ${pluralGroup} by the maximum of property values${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Median`,
        operator: PRIMITIVE_MATH_TYPES.MEDIAN,
        perUserOperator: PER_USER_AGGREGATIONS.MAX,
        description: ({group}: AggregationLabelParams) =>
          `Median of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `25th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P25,
        perUserOperator: PER_USER_AGGREGATIONS.MAX,
        shortLabel: () => `P25`,
        description: ({group}: AggregationLabelParams) =>
          `The 25th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `75th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P75,
        perUserOperator: PER_USER_AGGREGATIONS.MAX,
        shortLabel: () => `P75`,
        description: ({group}: AggregationLabelParams) =>
          `The 75th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `90th Percentile`,
        operator: PRIMITIVE_MATH_TYPES.P90,
        perUserOperator: PER_USER_AGGREGATIONS.MAX,
        shortLabel: () => `P90`,
        description: ({group}: AggregationLabelParams) =>
          `The 90th percentile of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
      {
        forProperty: true,
        label: () => `Minimum`,
        operator: PRIMITIVE_MATH_TYPES.MIN,
        perUserOperator: PER_USER_AGGREGATIONS.MAX,
        description: ({group}: AggregationLabelParams) =>
          `Minimum of the maximum of property values per ${group}${HELP_DOCS_LINK}`,
      },
    ],
  },
];

const UserPropertyAggregations: SelectorTypeAggregation[] = [
  {
    forProperty: true,
    label: () => `Sum`,
    operator: PRIMITIVE_MATH_TYPES.TOTAL,
    description: () => `Counts the total of a numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `Average`,
    operator: PRIMITIVE_MATH_TYPES.AVERAGE,
    description: () =>
      `Adds the numeric property values across all profiles and divides by the total count${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `Median`,
    operator: PRIMITIVE_MATH_TYPES.MEDIAN,
    description: () =>
      `Counts the numeric property value across all profiles in order and finds the middle value${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `Distinct Count`,
    operator: PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
    description: () => `The number of distinct property values across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `25th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P25,
    shortLabel: () => `P25`,
    description: () => `Ranks the 25th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `75th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P75,
    shortLabel: () => `P75`,
    description: () => `Ranks the 75th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `90th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P90,
    shortLabel: () => `P90`,
    description: () => `Ranks the 90th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `99th Percentile`,
    operator: PRIMITIVE_MATH_TYPES.P99,
    shortLabel: () => `P99`,
    description: () => `Ranks the 99th percentile of the numeric property value across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `Minimum`,
    operator: PRIMITIVE_MATH_TYPES.MIN,
    description: () => `The smallest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
  {
    forProperty: true,
    label: () => `Maximum`,
    operator: PRIMITIVE_MATH_TYPES.MAX,
    description: () => `The greatest value of a numeric property across all profiles${HELP_DOCS_LINK}`,
  },
];

const EVENT_BASED_AGGREGATIONS = new Set<SelectorTypeAggregation>([
  ...EventAggregations,
  ...EventPropertyAggregations,
  ...PerUserEventPropertyAggregations,
]);

const USER_BASED_AGGREGATIONS = new Set<SelectorTypeAggregation>([...UserAggregations, ...UserPropertyAggregations]);

export const AggregationsMappings = new Map<SelectorType, Set<SelectorTypeAggregation>>([
  [ResourceType.Event, EVENT_BASED_AGGREGATIONS],
  [
    SelectorTypeDefinitions.Behavior,
    new Set([...Array.from(EVENT_BASED_AGGREGATIONS), ...BehaviorPropertyAggregations]),
  ],
  [ResourceType.User, USER_BASED_AGGREGATIONS],
]);

/* Defines the items to be displayed in an aggregation menu for all entity types */
export const NESTED_AGGREGATIONS_MENU_ITEMS: SelectorTypeAggregation[] = [
  // Total
  EventAggregations.find((item) => item.operator === PRIMITIVE_MATH_TYPES.TOTAL),
  // Uniques
  EventAggregations.find((item) => item.operator === PRIMITIVE_MATH_TYPES.UNIQUE),
  // Profile count for people mode
  UserAggregations.find((item) => item.operator === PRIMITIVE_MATH_TYPES.TOTAL),
  // Total per user
  EventAggregations.find((item) => item.perUserOperator === PER_USER_AGGREGATIONS.TOTAL),
  // Event property aggregations
  {
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.count-menu.agg-prop`)),
    description: () => `Aggregate property value across all instances of an event${HELP_DOCS_LINK}`,
    subSections: EventPropertyAggregations,
  },
  // Profile property aggregations
  {
    label: () => translatable(translationKeys, ({t}) => t(`query-builder.count-menu.agg-prop`)),
    description: () => `Aggregate property value across all profiles${HELP_DOCS_LINK}`,
    subSections: UserPropertyAggregations,
  },
  // Rolling uniques (DAU, WAU, MAU)
  {
    label: ({pluralGroupTitle}: AggregationLabelParams) => `Count ${pluralGroupTitle}`,
    description: ({pluralGroup}: AggregationLabelParams) =>
      `The number of ${pluralGroup} who performed an event within a time period${HELP_DOCS_LINK}`,
    subSections: EventAggregations.filter(
      (item) =>
        item.operator === XAU_MATH_TYPES.DAU ||
        item.operator === XAU_MATH_TYPES.WAU ||
        item.operator === XAU_MATH_TYPES.MAU,
    ),
  },
  // Per-user event property aggregations
  {
    label: ({group}: AggregationLabelParams) => `Aggregate Properties per ${group}`,
    description: ({group}: AggregationLabelParams) =>
      `Aggregate property values across all instances of an event per ${group}${HELP_DOCS_LINK}`,
    subSections: PerUserEventPropertyAggregations,
  },
  // Session count
  EventAggregations.find((item) => item.operator === PRIMITIVE_MATH_TYPES.SESSIONS),
];
